import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../Modal/Modal";
import { Button } from "../Button/Button";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import { FormField } from "../FormField/FormField";
import { useSapCountries, useSapStates, useAddressDistricts } from "../../../hooks/use-companies";
import styles from "./ShippingAddressFormModal.module.css";

export interface ShippingAddressFormValues {
	addressName: string;
	addressName2: string;
	addressName3: string;
	postalCode: string;
	city: string;
	state: string;
	country: string;
}

const EMPTY_FORM: ShippingAddressFormValues = {
	addressName: "",
	addressName2: "",
	addressName3: "",
	postalCode: "",
	city: "",
	state: "",
	country: "TÜRKİYE",
};

interface ShippingAddressFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	initialValues?: ShippingAddressFormValues | undefined;
	onSubmit: (values: ShippingAddressFormValues) => void;
	isSubmitting?: boolean | undefined;
	submitError?: string | undefined;
}

export function ShippingAddressFormModal({
	isOpen,
	onClose,
	title,
	initialValues,
	onSubmit,
	isSubmitting,
	submitError,
}: ShippingAddressFormModalProps) {
	const { t } = useTranslation();
	// Parent bu component'i `key` ile her açılışta remount ediyor (bkz. kullanım yerleri),
	// bu yüzden state'i burada effect'le senkronize etmeye gerek yok — ilk mount değeri yeterli.
	const [form, setForm] = useState<ShippingAddressFormValues>(initialValues ?? EMPTY_FORM);
	const [error, setError] = useState("");

	const countries = useSapCountries();
	const countryCode = countries.data?.find((c) => c.name === form.country)?.code;
	const states = useSapStates(countryCode);
	const stateCode = states.data?.find((s) => s.name === form.state)?.code;
	const districts = useAddressDistricts(stateCode);

	// 100'den sonrası otomatik olarak "Adres Devamı" (addressName3 / Block) alanına taşınır.
	const handleAddressLineChange = (value: string) => {
		setForm((prev) => {
			if (value.length <= 100) return { ...prev, addressName2: value };
			const overflow = value.slice(100).trimStart();
			return {
				...prev,
				addressName2: value.slice(0, 100).trimEnd(),
				addressName3: (overflow + prev.addressName3).slice(0, 100),
			};
		});
	};

	const handleSubmit = () => {
		if (!form.addressName.trim()) {
			setError(t("fillRequiredFields"));
			return;
		}
		setError("");
		onSubmit({ ...form, addressName: form.addressName.trim() });
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth={520}>
			<div className={styles.form}>
				{(error || submitError) && <div className={styles.error}>{error || submitError}</div>}
				<FormField label={t("addressName")} required>
					<Input
						value={form.addressName}
						maxLength={50}
						onChange={(e) => setForm({ ...form, addressName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("addressLine")}>
					<Input
						value={form.addressName2}
						onChange={(e) => handleAddressLineChange(e.target.value)}
					/>
				</FormField>
				<FormField label={t("addressLineCont")}>
					<Input
						value={form.addressName3}
						maxLength={100}
						onChange={(e) => setForm({ ...form, addressName3: e.target.value })}
					/>
				</FormField>
				<FormField label={t("country")}>
					<Select
						options={[
							{ value: "", label: "-" },
							...(countries.data ?? []).map((c) => ({ value: c.name, label: c.name })),
						]}
						value={form.country}
						onChange={(e) => setForm({ ...form, country: e.target.value, state: "", city: "" })}
					/>
				</FormField>
				<FormField label={t("province")}>
					<Select
						options={[
							{ value: "", label: "-" },
							...(states.data ?? []).map((s) => ({ value: s.name, label: s.name })),
						]}
						value={form.state}
						onChange={(e) => setForm({ ...form, state: e.target.value, city: "" })}
						disabled={states.isLoading}
					/>
				</FormField>
				<FormField label={t("district")}>
					{/* SAP'ta ilçe kod tablosu yok; mevcut adreslerden öneri sunulur, elle de yazılabilir. */}
					<Input
						value={form.city}
						maxLength={100}
						list="shipping-address-form-districts"
						disabled={!form.state}
						placeholder={form.state ? "" : t("selectProvinceFirst")}
						onChange={(e) => setForm({ ...form, city: e.target.value })}
					/>
					<datalist id="shipping-address-form-districts">
						{(districts.data?.items ?? []).map((d) => (
							<option key={d} value={d} />
						))}
					</datalist>
				</FormField>
				<FormField label={t("postalCode")}>
					<Input
						value={form.postalCode}
						maxLength={20}
						onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
					/>
				</FormField>
				<div className={styles.actions}>
					<Button type="button" variant="secondary" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
						{t("save")}
					</Button>
				</div>
			</div>
		</Modal>
	);
}
